import axios, { AxiosResponse } from "axios"

const api = axios.create({
  baseURL: process.env.REACT_APP_MGMT_SVC_URL,
  withCredentials: true,
})

export const suggestLocale = async (): Promise<string> => {
  const response = await api.get("/suggest_locale")
  return response.data.suggested_locale
}

export const checkUserExists = async (email: string): Promise<boolean> => {
  const response = await api.post("/check_user", { email })
  // The response from /check_user is a single JSON encoded boolean
  return response.data
}

export const login = async (
  email: string,
  password: string,
): Promise<boolean> => {
  try {
    await api.post("/login", new URLSearchParams({ email, password }))
    return true
  } catch (error) {
    if (
      axios.isAxiosError(error) &&
      error.response &&
      error.response.status === 401
    ) {
      // A 401 response might be fixable by the user.
      return false
    } else {
      // Other errors not so much.
      throw error
    }
  }
}

export const createUser = async (
  email: string,
  password: string,
  assignedLocale: string | undefined,
): Promise<void> => {
  await api.post("/create_user", {
    email,
    password,
    assigned_locale: assignedLocale,
  })
}

export const promoCodeCheck = async (identifier: string): Promise<void> => {
  await postAndProcessBadRequest("/promo_codes/check", { identifier })
}

export const promoCodeRedeem = async (identifier: string): Promise<string> => {
  const response = await postAndProcessBadRequest("/promo_codes/redeem", {
    identifier,
  })
  return response.data.reward_duration
}

export const gympassPrecheckActivate = async (
  gympassToken: string,
): Promise<void> => {
  await postAndProcessBadRequest("/gympass/precheck_activate", {
    token: gympassToken,
  })
}

export const gympassActivate = async (gympassToken: string): Promise<void> => {
  await postAndProcessBadRequest("/gympass/activate", {
    token: gympassToken,
  })
}

export const fitnessOnDemandCheckToken = async (
  token: string,
): Promise<void> => {
  await postAndProcessBadRequest("/fitness_on_demand/check_token", {
    token,
  })
}

export const fitnessOnDemandSaveLink = async (token: string): Promise<void> => {
  await postAndProcessBadRequest("/fitness_on_demand/save_link", {
    token,
  })
}

export const promoLinkCheckToken = async (token: string): Promise<void> => {
  await postAndProcessBadRequest("/promo_link/check_token", { token })
}

export const promoLinkRedeem = async (token: string): Promise<void> => {
  await postAndProcessBadRequest("/promo_link/redeem", { token })
}

const postAndProcessBadRequest = async (
  endpoint: string,
  body: unknown,
): Promise<AxiosResponse> => {
  try {
    return await api.post(endpoint, body)
  } catch (error) {
    // Some specific 400 responses have a message that we want to wrap usefully.
    if (
      axios.isAxiosError(error) &&
      error.response &&
      error.response.status === 400 &&
      hasMessageString(error.response.data)
    ) {
      throw new ApiError(error.response.data.message)
    } else {
      throw error
    }
  }
}

const hasMessageString = (data: any): data is { message: string } =>
  typeof data?.message === "string"

export class ApiError extends Error {}
